import { useState, useEffect, useMemo, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { httpClient } from '../services/httpClient'

// Using httpClient which handles API base URL and authentication

export interface SectionCompletion {
  sectionLabel: string
  totalFields: number
  requiredFields: number
  completedFields: number
  completedRequired: number
  isComplete: boolean
  completionPercentage: number
}

export interface ProfileStatusData {
  overallCompletion: number
  isComplete: boolean
  sections: Record<string, SectionCompletion>
  completedSections: string[]
}

// HttpClient automatically extracts data, so we get ProfileStatusData directly
type ProfileStatusApiResponse = ProfileStatusData

interface ProfileCompletionStatus {
  isProfileComplete: boolean
  isCheckingCompletion: boolean
  hasCheckedCompletion: boolean
  requiresProfileCompletion: boolean
  hasUserData: boolean
  error: string | null
  refreshCompletion: () => void
}

export function useProfileCompletion(): ProfileCompletionStatus {
  const { user, isLoading: authLoading, refreshUserData } = useAuth()
  const [isProfileComplete, setIsProfileComplete] = useState<boolean>(false)
  const [isCheckingCompletion, setIsCheckingCompletion] = useState<boolean>(false)
  const [hasCheckedCompletion, setHasCheckedCompletion] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0)
  const [isFirstCheck, setIsFirstCheck] = useState<boolean>(true)

  // Track if we have reliable user data to determine requirements
  const hasUserData = !authLoading && user !== null

  // Memoize the profile completion requirement to prevent infinite re-renders
  const requiresProfileCompletion = useMemo(() => {
    return Boolean(
      hasUserData &&
      user && 
      user.role === 'user' && 
      user.type === 'smb'
    )
  }, [hasUserData, user?.role, user?.type])

  // CRITICAL FIX: Memoize refreshCompletion to prevent infinite re-renders
  const refreshCompletion = useCallback(() => {
    setRefreshTrigger(prev => prev + 1)
  }, [])

  useEffect(() => {
    const checkProfileCompletion = async () => {
      if (authLoading || !user) {
        // Reset states when we don't have user data
        setIsProfileComplete(false)
        setHasCheckedCompletion(false)
        setIsFirstCheck(true)
        return
      }

      // If user doesn't require profile completion, mark as complete and checked
      if (!requiresProfileCompletion) {
        setIsProfileComplete(true)
        setHasCheckedCompletion(true)
        setIsFirstCheck(false)
        return
      }

      // PERFORMANCE OPTIMIZATION: Check User.profileCompleted first
      // This eliminates API calls and DB queries for users with completed profiles
      if (user.profileCompleted === true) {
        // Only log on first check to reduce console spam (non-production only)
        if ((import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') && isFirstCheck) {
          console.debug('Profile completion: User already has completed profile (User.profileCompleted === true)')
        }
        setIsProfileComplete(true)
        setHasCheckedCompletion(true)
        setIsFirstCheck(false)
        setError(null)
        return
      }

      // If User.profileCompleted is explicitly false, don't make API call
      if (user.profileCompleted === false) {
        // Only log on first check to reduce console spam (non-production only)
        if ((import.meta.env.DEV || import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging') && isFirstCheck) {
          console.debug('Profile completion: User profile is incomplete (User.profileCompleted === false)')
        }
        setIsProfileComplete(false)
        setHasCheckedCompletion(true)
        setIsFirstCheck(false)
        setError(null)
        return
      }

      // Only make API call if User.profileCompleted is undefined (edge case)
      // or if we need detailed profile status information
      try {
        setIsCheckingCompletion(true)
        setError(null)

        // Get profile completion status
        const response = await httpClient.get<ProfileStatusApiResponse>('/api/profile/my-profile/status')

        // HttpClient automatically extracts the data from the API response
        // So response is the actual data object, not wrapped in {success: true, data: {...}}
        setIsProfileComplete(response.isComplete ?? false)
      } catch (err) {
        console.error('Error checking profile completion:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
        
        // Defensive error handling - don't automatically assume profile is incomplete
        if (requiresProfileCompletion) {
          // Only assume incomplete for users who actually need profile completion
          setIsProfileComplete(false)
        } else {
          // For non-SMB users, assume complete on error to avoid blocking access
          setIsProfileComplete(true)
        }
      } finally {
        setIsCheckingCompletion(false)
        setHasCheckedCompletion(true)
        setIsFirstCheck(false)
      }
    }

    checkProfileCompletion()
  }, [user, authLoading, requiresProfileCompletion, refreshTrigger, isFirstCheck])

  // Listen for global refresh events
  useEffect(() => {
    const handleRefresh = () => {
      refreshCompletion()
    }

    const handleProfileCompletionChanged = async () => {
      // When profile completion changes, wait for AuthContext to refresh user data
      // then refresh the completion status
      try {
        // Wait for AuthContext to refresh user data first
        await refreshUserData()
        
        // Now refresh completion status with the updated user data
        refreshCompletion()
      } catch (error) {
        console.error('Error refreshing user data before profile completion check:', error)
        
        // Fallback: still try to refresh completion even if user data refresh failed
        try {
          refreshCompletion()
        } catch (refreshError) {
          console.error('Error refreshing profile completion:', refreshError)
        }
      }
    }

    window.addEventListener('refreshProfileCompletion', handleRefresh)
    window.addEventListener('profileCompletionChanged', handleProfileCompletionChanged)
    
    return () => {
      window.removeEventListener('refreshProfileCompletion', handleRefresh)
      window.removeEventListener('profileCompletionChanged', handleProfileCompletionChanged)
    }
    // CRITICAL FIX: Remove refreshCompletion from dependency array since it's now memoized
    // Include refreshUserData in dependencies since we use it in the event handler
  }, [refreshUserData, refreshCompletion])

  return {
    isProfileComplete,
    isCheckingCompletion,
    hasCheckedCompletion,
    requiresProfileCompletion,
    hasUserData,
    error,
    refreshCompletion
  }
} 